import { openAnswersProperty, hasOpenAnswers } from "../OpenAnswers";
import { v4 as uuidv4 } from "uuid";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import {
  InteractionFactoryEntity,
  ReconcileContext,
} from "../../features/interaction_factory/InteractionFactory";
import PiBasicQuizPreview from "../../features/previews/PiBasicQuizPreview";
import { GetMigratedProperties } from "../../features/project_migration/InteractionMigrationHelper";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { Actor } from "../Actor";
import { requiredGlobalQuizProperties } from "../GlobalQuizProperty";
import { InteractionProperty } from "../InteractionProperty";
import { IAnswer, IFormQuiz } from "../Quiz";
import {
  collectQuizFeedbackSearchableStrings,
  copyQuizFeedback,
  createEmptyQuizFeedback,
  migrateQuizFeedback,
  reconcileQuizFeedback,
  validateQuizFeedback,
  validateQuizFeedbackErrors,
} from "../QuizFeedbackHelper";
import Take from "../Take";
import Interaction, { InteractionType } from "./Interaction";
import { LocalizedStringRecord } from "../../app/slices/TrainingLocalizationSlice";
import { Prop } from "../Prop";

export const requiredMultipleChoiceGraphicsProperties: InteractionProperty[] = [
  ...requiredGlobalQuizProperties,
  openAnswersProperty,
];

export function CreateMultipleChoiceGraphicsQuiz(keys: string[]): IFormQuiz {
  return {
    id: uuidv4(),
    name: "Multiple Choice Graphcis Quiz",
    type: InteractionType.MultipleChoiceGraphicsQuiz,
    title: keys[0],
    answers: [
      { id: uuidv4(), correct: true, text: keys[1] },
      { id: uuidv4(), correct: false, text: keys[2] },
    ],
    properties: requiredMultipleChoiceGraphicsProperties,
    ...createEmptyQuizFeedback(),
  };
}

export function CreateMultipleChoiceGraphicsQuizCopy(
  titleKey: string,
  answers: IAnswer[],
  properties: InteractionProperty[] | undefined
): IFormQuiz {
  return {
    id: uuidv4(),
    name: "Multiple Choice Graphcis Quiz",
    type: InteractionType.MultipleChoiceGraphicsQuiz,
    title: titleKey,
    answers: answers,
    properties: properties,
  };
}

export function MigrateMultipleChoiceGraphicsQuiz(
  from: number,
  to: number,
  interaction: IFormQuiz
): IFormQuiz {
  if (interaction.properties === undefined)
    return {
      ...interaction,
      properties: requiredMultipleChoiceGraphicsProperties,
      ...migrateQuizFeedback(from, to, interaction),
    };

  var newProperties: InteractionProperty[] = GetMigratedProperties(
    requiredMultipleChoiceGraphicsProperties,
    interaction.properties
  );
  console.log(
    "[MultipleChoiceGraphicsQuiz] migrated " +
      (newProperties.length - interaction.properties.length) +
      " properties"
  );

  return {
    ...interaction,
    properties: newProperties,
    ...migrateQuizFeedback(from, to, interaction),
  };
}

export const multipleChoiceGraphicsQuizInteraction =
  (): InteractionFactoryEntity => {
    const component = <PiBasicQuizPreview displayGraphics />;

    const create = (getKeys: (amount: number) => string[]): IFormQuiz => {
      const keys = getKeys(3);

      return {
        id: uuidv4(),
        name: "Multiple Choice Graphcis Quiz",
        type: InteractionType.MultipleChoiceGraphicsQuiz,
        title: keys[0],
        answers: [
          { id: uuidv4(), correct: true, text: keys[1], assetId: "" },
          { id: uuidv4(), correct: false, text: keys[2], assetId: "" },
        ],
        properties: requiredMultipleChoiceGraphicsProperties,
        ...createEmptyQuizFeedback(),
      };
    };

    const migrate = (
      from: number,
      to: number,
      interaction: Interaction
    ): IFormQuiz => {
      const quizData = interaction as IFormQuiz; // need to cast

      if (interaction.properties === undefined)
        return {
          ...quizData,
          properties: requiredMultipleChoiceGraphicsProperties,
          ...migrateQuizFeedback(from, to, quizData),
        };

      var newProperties: InteractionProperty[] = GetMigratedProperties(
        requiredMultipleChoiceGraphicsProperties,
        interaction.properties
      );
      console.log(
        "[MultipleChoiceGraphicsQuiz] migrated " +
          (newProperties.length - interaction.properties.length) +
          " properties"
      );

      return {
        ...quizData,
        properties: newProperties,
        ...migrateQuizFeedback(from, to, quizData),
      };
    };

    const copy = (
      interaction: Interaction,
      getKeys: (amount: number) => string[],
      replaceKey: (from: string, to: string) => void,
      getAnswerArray: (
        originalAnswers: IAnswer[],
        newKeys: string[]
      ) => IAnswer[]
    ): IFormQuiz => {
      const formQuiz = interaction as IFormQuiz;
      const newKeys = getKeys(formQuiz.answers.length + 1);
      replaceKey(formQuiz.title, newKeys[newKeys.length - 1]);
      return {
        id: uuidv4(),
        name: interaction.name,
        type: InteractionType.MultipleChoiceGraphicsQuiz,
        title: newKeys[newKeys.length - 1],
        answers: getAnswerArray(formQuiz.answers, newKeys),
        properties: formQuiz.properties,
        ...copyQuizFeedback(formQuiz, getKeys, replaceKey, getAnswerArray),
      };
    };

    const validate = (
      interaction: Interaction,
      getValue: (key: string) => string,
      take: Take,
      actorPresets: Actor[]
    ): string[] => {
      const converted = interaction as IFormQuiz;
      const warnings: string[] = [];

      ValidationHelper.ValidateKey(
        converted.title,
        getValue,
        warnings,
        "Title"
      );

      // validate all answers + assetIds + validate we have at least one answer
      for (let i = 0; i < converted.answers.length; i++) {
        const tempAnswer = converted.answers[i];
        ValidationHelper.ValidateKey(
          tempAnswer.text,
          getValue,
          warnings,
          `Answer ${i + 1}`
        );
      }

      warnings.push(
        ...validateQuizFeedback(converted, getValue, take, actorPresets)
      );

      return warnings;
    };

    const filter = (
      interaction: Interaction,
      takeName: string,
      getMatchingResults: (
        searchableStrings: string[],
        takeName: string,
        interaction: Interaction,
        filterType: string
      ) => SearchResultData[]
    ): SearchResultData[] => {
      const converted = interaction as IFormQuiz;
      const searchableStrings: string[] = [
        converted.name,
        converted.title,
        ...converted.answers.map((item) => item.text),
        ...collectQuizFeedbackSearchableStrings(converted),
      ];

      return getMatchingResults(
        searchableStrings,
        takeName,
        interaction,
        filterType
      );
    };

    const filterType: string = "MultipleChoice Graphics Quiz";

    const validateErrors = (
      interaction: Interaction,
      parentTake: Take,
      currentTake: Take,
      actorPresets: Actor[],
      allPropPresets: Prop[],
      localizedStringRecords: LocalizedStringRecord
    ): string[] => {
      const converted = interaction as IFormQuiz;
      const errors: string[] = [];

      const count = converted.answers.filter((item) => item.correct).length;
      if (converted.answers.length === 0) errors.push("Please add an answer");

    if (!hasOpenAnswers(converted) && count === 0) {
        errors.push("Please add a correct answer");
      }

      for (let i = 0; i < converted.answers.length; i++) {
        const tempAnswer = converted.answers[i];

        ValidationHelper.ValidateAssetId(
          tempAnswer.assetId ?? "",
          errors,
          `Answer ${i + 1} image`
        );
      }

      errors.push(
        ...validateQuizFeedbackErrors(
          converted,
          parentTake,
          currentTake,
          actorPresets,
          allPropPresets,
          localizedStringRecords
        )
      );

      return errors;
    };

    const reconcile = (
      interaction: Interaction,
      context: ReconcileContext
    ): Interaction => {
      return reconcileQuizFeedback(interaction as IFormQuiz, context);
    };

    return {
      View: component,
      FilterType: filterType,
      Create: create,
      Migrate: migrate,
      Copy: copy,
      Reconcile: reconcile,
      Validate: validate,
      Filter: filter,
      ValidateErrors: validateErrors,
    };
  };
